import React from 'react';
import { Trans } from '@lingui/react/macro';
import { faImage } from '@fortawesome/pro-light-svg-icons';
import { List } from '@ygtechnologies/rubus-ui';

import { BuyerCurrencyDisplay } from '@/buyer/components/BuyerCurrencyDisplay';
import {
    ConfiguratorGroupDto,
    ConfiguratorSignatureDto,
} from '@/services/configurator';
import { getProjectCatalogProductFileThumbnailUrl } from '@/services/project-catalog';
import { getOptionPresetImageThumbnailUrl } from '@/services/option-preset/option-preset-image';

import theme from './SubmitWizard.module.scss';

interface Props {
    signatureInfo: ConfiguratorSignatureDto;
}

interface RowProps {
    group: ConfiguratorGroupDto;
}

const firstImageId = (images: { id: number; sortOrder: number }[] | undefined) =>
    images && images.length > 0
        ? [...images].sort((a, b) => a.sortOrder - b.sortOrder)[0].id
        : null;

const ConfirmRow: React.FC<RowProps> = ({ group }) => {
    const selected = group.selectedOption;
    const imageId = firstImageId(selected?.product?.images);
    const src = imageId != null ? getProjectCatalogProductFileThumbnailUrl(imageId) : undefined;
    return (
        <List.Item>
            <List.Thumbnail src={src} icon={faImage} size="md" alt={group.name} />
            <List.Content
                title={group.name}
                subtitle={
                    <>
                        <Trans>Your choice:</Trans>{' '}
                        <strong>{selected?.productName ?? ''}</strong>{' '}
                        <i>{selected?.customFieldData ?? ''}</i>
                    </>
                }
            />
            <List.Trailing>
                {!group.hidePrice && selected?.price && (
                    <span className={theme.confirmPrice}>
                        <BuyerCurrencyDisplay value={selected.price.finalPrice} alwaysShowSign />
                    </span>
                )}
            </List.Trailing>
        </List.Item>
    );
};

export const ConfirmUpgrades: React.FC<Props> = ({ signatureInfo }) => {
    return (
        <>
            <List>
                {signatureInfo.optionGroups.map((group) => (
                    <ConfirmRow key={`${group.id}-${group.roomId}`} group={group} />
                ))}

                {signatureInfo.presetGroups.flatMap((pg) =>
                    pg.styles
                        .filter((s) => s.isPicked)
                        .map((style) => {
                            const imageId = firstImageId(style.images);
                            const src =
                                imageId != null ? getOptionPresetImageThumbnailUrl(imageId) : undefined;
                            return (
                                <List.Item key={`${pg.id}-${style.id}`}>
                                    <List.Thumbnail
                                        src={src}
                                        icon={faImage}
                                        size="md"
                                        alt={style.name}
                                    />
                                    <List.Content title={style.name} />
                                    <List.Trailing>
                                        {style.price != null && (
                                            <span className={theme.confirmPrice}>
                                                <BuyerCurrencyDisplay value={style.price} />
                                            </span>
                                        )}
                                    </List.Trailing>
                                </List.Item>
                            );
                        })
                )}
            </List>

            <div className={theme.totalCard}>
                <span>
                    <Trans>Total</Trans>
                </span>
                <span>
                    <BuyerCurrencyDisplay value={signatureInfo.totalPrice} />
                </span>
            </div>
        </>
    );
};
